import { useMutation } from '@tanstack/react-query'

import type { StreamChatRequest } from '../../types/index.js'

import { apiClient } from '../../lib/client.js'
import { chatKeys } from './keys.js'

export interface StreamChatMutationResult {
  content: string
}

export interface StreamChatMutationVariables extends StreamChatRequest {
  onChunk?: (chunk: string) => void
  signal?: AbortSignal
}

const MAX_STREAM_URL_LENGTH = 7000
const SSE_DATA_PREFIX = 'data:'

export async function streamChatMutationFn({
  messages,
  onChunk,
  signal,
}: StreamChatMutationVariables): Promise<StreamChatMutationResult> {
  const baseUrl = apiClient.client.defaults.baseURL ?? ''
  const params = new URLSearchParams({
    messages: JSON.stringify(messages),
  })

  const streamUrl = `${baseUrl}/chat/stream?${params.toString()}`
  if (streamUrl.length > MAX_STREAM_URL_LENGTH) {
    throw new Error(
      'Conversation trop longue pour le mode streaming. Merci de redemarrer une nouvelle conversation.'
    )
  }

  const response = await fetch(streamUrl, {
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    let backendMessage: string | undefined

    try {
      const payload = (await response.json()) as { message?: string }
      if (typeof payload.message === 'string') {
        backendMessage = payload.message
      }
    } catch {
      const fallbackText = await response.text().catch(() => '')
      if (fallbackText) {
        backendMessage = fallbackText
      }
    }

    if (response.status === 429) {
      throw new Error(backendMessage ?? 'Trop de messages envoyés. Veuillez patienter.')
    }

    throw new Error(backendMessage ?? 'Stream connection failed')
  }

  if (!response.body) {
    throw new Error('Stream connection failed')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let content = ''
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const events = buffer.split('\n\n')
    buffer = events.pop() ?? ''

    for (const event of events) {
      const chunk = extractSseEventData(event)
      if (chunk === null || chunk === '[DONE]') {
        continue
      }

      content += chunk
      onChunk?.(chunk)
    }
  }

  if (buffer) {
    const chunk = extractSseEventData(buffer)
    if (chunk !== null && chunk !== '[DONE]') {
      content += chunk
      onChunk?.(chunk)
    }
  }

  return { content }
}

export function useStreamChatMutation() {
  return useMutation({
    mutationFn: streamChatMutationFn,
    mutationKey: chatKeys.stream,
  })
}

function extractSseData(line: string): null | string {
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return null
  }

  // SSE allows exactly one optional space after "data:".
  const raw = line.slice(SSE_DATA_PREFIX.length)
  return raw.startsWith(' ') ? raw.slice(1) : raw
}

function extractSseEventData(event: string): null | string {
  const lines = event.split('\n')
  const dataLines: string[] = []

  for (const line of lines) {
    const chunk = extractSseData(line)
    if (chunk === null) {
      continue
    }

    dataLines.push(chunk)
  }

  if (dataLines.length === 0) {
    return null
  }

  // SSE spec: an event with multiple `data:` lines joins them with "\n".
  return dataLines.join('\n')
}
